import { useAppContext } from "@/context/AppContext";
import {
  searchRecipesForResults,
  searchPagesForResults,
} from "@/utils/search/searchRecipesForResults";
import { useRouter } from "next/router";
import { useState, useCallback, useEffect } from "react";

export type SearchResultItemType = {
  value_category: number[];
  value_ernaehrungsform: number[];
  value_id: number;
  value_titel: string;
  value_type: string;
  is_subpage: boolean;
  value_link: string;
  value_slug: string;
  schwierigkeitsgrad: number;
  produkttyp: "Der kleine Tiptopf" | "Tiptopf";
};

export type TabData = {
  results: SearchResultItemType[];
  currentPage: number;
  totalPages: number;
  totalResults: number;
  isFullyLoaded: boolean; // To track if all pages are loaded
};

export const useSearch = () => {
  const { loading, setLoading } = useAppContext();
  const router = useRouter();
  const [currentTab, setCurrentTab] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");

  // Consolidated state for all tabs
  const [tabs, setTabs] = useState<Record<number, TabData>>({
    0: {
      results: [],
      currentPage: 1,
      totalPages: 0,
      totalResults: 0,
      isFullyLoaded: false,
    },
    1: {
      results: [],
      currentPage: 1,
      totalPages: 0,
      totalResults: 0,
      isFullyLoaded: false,
    },
    2: {
      results: [],
      currentPage: 1,
      totalPages: 0,
      totalResults: 0,
      isFullyLoaded: false,
    },
    3: {
      results: [],
      currentPage: 1,
      totalPages: 0,
      totalResults: 0,
      isFullyLoaded: false,
    },
  });

  const [visitedTabs, setVisitedTabs] = useState<Record<number, boolean>>({});

  const getSearchKeyword = useCallback(() => {
    if (typeof window === "undefined") return "";
    const urlParams = new URLSearchParams(window.location.search);
    return (urlParams.get("keyword") || "").replace(/\/+$/, "");
  }, []);

  // --- Data Fetching Functions (memoized with useCallback) ---
  const fetchResults = useCallback(
    async (keyword: string, type: string, page: number) => {
      return await searchRecipesForResults(keyword, type, page);
    },
    [],
  );

  const searchPages = useCallback(
    async (keyword: string) => {
      return await searchPagesForResults(keyword, router?.locale);
    },
    [router?.locale],
  );

  // --- Initial Data Loading Effect ---
  useEffect(() => {
    const searchKeyword = getSearchKeyword();
    setSearchKeyword(searchKeyword);
    const loadInitialData = async () => {
      // Don't run if tab has been visited or is already loading
      if (visitedTabs[currentTab] || loading) return;

      setLoading(true);
      setVisitedTabs((prev) => ({ ...prev, [currentTab]: true }));

      if (!searchKeyword) {
        setLoading(false);
        return;
      }

      try {
        let newResults: SearchResultItemType[] = [];
        let newTotalPages = 0;
        let newTotalResults = 0;

        if (currentTab === 0) {
          // All
          const [mainRes, pagesRes] = await Promise.all([
            fetchResults(searchKeyword, "", 1),
            searchPages(searchKeyword),
          ]);

          newResults = [
            ...(mainRes?.suchergebnisse || []),
            ...(pagesRes?.suchergebnisse || []),
          ];
          newTotalResults =
            (mainRes?.ergebnis_count || 0) + (pagesRes?.ergebnis_count || 0);
          newTotalPages = mainRes?.results_pages || 1;
        } else if (currentTab === 1) {
          // Recipes
          const res = await fetchResults(searchKeyword, "Rezept", 1);
          newResults = res?.suchergebnisse || [];
          newTotalPages = res?.results_pages || 1;
          newTotalResults = res?.ergebnis_count || 0;
        } else if (currentTab === 2) {
          // Ingredients
          const res = await fetchResults(searchKeyword, "Zutat", 1);
          newResults = res?.suchergebnisse || [];
          newTotalPages = res?.results_pages || 1;
          newTotalResults = res?.ergebnis_count || 0;
        } else if (currentTab === 3) {
          // Other (Sequential fetch)
          const kategorieRes = await fetchResults(
            searchKeyword,
            "Kategorie",
            1,
          );

          newResults.push(...(kategorieRes?.suchergebnisse || []));
          newTotalResults += kategorieRes?.ergebnis_count || 0;
          const pagesRes = await searchPages(searchKeyword);
          newResults.push(...(pagesRes?.suchergebnisse || []));
          newTotalResults += pagesRes?.ergebnis_count || 0;

          if (newTotalResults <= 10) {
            const garmethodeRes = await fetchResults(
              searchKeyword,
              "Garmethode",
              1,
            );
            newResults.push(...(garmethodeRes?.suchergebnisse || []));
            newTotalResults += garmethodeRes?.ergebnis_count || 0;
          }
          if (newTotalResults <= 10) {
            const saisonRes = await fetchResults(searchKeyword, "Saison", 1);
            newResults.push(...(saisonRes?.suchergebnisse || []));
            newTotalResults += saisonRes?.ergebnis_count || 0;
          }
        }

        setTabs((prev) => ({
          ...prev,
          [currentTab]: {
            ...prev[currentTab],
            results: newResults,
            totalPages: newTotalPages,
            totalResults: newTotalResults,
            isFullyLoaded: newTotalPages <= 1,
          },
        }));
      } catch (error) {
        console.error("Failed to fetch initial search results:", error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, getSearchKeyword]); // Dependencies are stable

  // --- "Load More" Functionality ---
  const fetchMore = async () => {
    const tabState = tabs[currentTab];
    if (loading || tabState.isFullyLoaded) return;

    setLoading(true);
    const searchKeyword = getSearchKeyword();
    const nextPage = tabState.currentPage + 1;

    const types = ["", "Rezept", "Zutat", "Kategorie"];
    const apiType = types[currentTab];

    try {
      const res = await fetchResults(searchKeyword, apiType, nextPage);
      if (res && res.suchergebnisse.length > 0) {
        setTabs((prev) => ({
          ...prev,
          [currentTab]: {
            ...prev[currentTab],
            results: [...prev[currentTab].results, ...res.suchergebnisse],
            currentPage: nextPage,
            isFullyLoaded:
              nextPage >= (res.results_pages || tabState.totalPages),
          },
        }));
      } else {
        // No more results from this source
        setTabs((prev) => ({
          ...prev,
          [currentTab]: { ...prev[currentTab], isFullyLoaded: true },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch more results:", error);
    } finally {
      setLoading(false);
    }
  };

  return { loading, currentTab, setCurrentTab, tabs, fetchMore, searchKeyword };
};
